import { getTimeSlotsClass, getSlotList } from "./TarifUtils";
import { useMantineColorScheme } from "@mantine/core";
import * as d3 from 'd3';
import { useEffect, useRef, useState, useCallback } from 'react';
import './TimeSlotDisplay.css';
import { useDualTranslation } from '../utils/i18nHelpers';

// Constants mirroring the DemandCharge D3 implementation
const TOTAL_MINUTES = 24 * 60; // 1440
const SLOT_MIN = 15;
const SLOT_COUNT = TOTAL_MINUTES / SLOT_MIN; // 96
const CELL_HEIGHT = 32; // slightly smaller to fit existing layout
const AXIS_HEIGHT = 22;
const MARGIN = { top: 4, right: 10, bottom: AXIS_HEIGHT + 2, left: 12 };

const getPeriodMatch = (item, startMin, index) => {
  const toMinutes = (slot) => {
    if (!slot) return [];
    return getSlotList(slot);
  };
  if (Array.isArray(item?.timeSlots) && item.timeSlots.some((ts) => toMinutes(ts).includes(startMin))) {
    return {
      periodColor: item.periodColor,
      periodLabel: item.periodName || item.name || `Period ${index + 1}`,
    };
  }
  if (item?.timeSlot && toMinutes(item.timeSlot).includes(startMin)) {
    return {
      periodColor: item.periodColor,
      periodLabel: item.periodName || item.name || `Period ${index + 1}`,
    };
  }
  return null;
};

const formatTimeLabel = (start) => {
  const hour = Math.floor(start / 60);
  const minute = start % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const TimeSlotsDisplay = ({ selectedSeason, activeDayGroup }) => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const { t } = useDualTranslation();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipFrameRef = useRef(null);
  const [width, setWidth] = useState(800);

  const activePeriodGroup = selectedSeason?.periods?.[activeDayGroup];
  const slotList = activePeriodGroup?.rate;

  // Deep signature for change detection
  const slotSignature = (() => {
    if (!Array.isArray(slotList)) return 'none';
    try {
      return slotList.map(item => {
        const segs = [];
        if (Array.isArray(item?.timeSlots)) {
          for (const ts of item.timeSlots) {
            const s = ts?.startTime || {}; const e = ts?.endTime || {};
            segs.push(`${s.hour??'x'}:${s.minute??'x'}-${e.hour??'x'}:${e.minute??'x'}`);
          }
        }
        if (item?.timeSlot) {
          const s = item.timeSlot.startTime || {}; const e = item.timeSlot.endTime || {};
          segs.push(`${s.hour??'x'}:${s.minute??'x'}-${e.hour??'x'}:${e.minute??'x'}`);
        }
        return segs.join('|') + (item.periodColor || '');
      }).join(';');
    } catch { return 'err'; }
  })();

  const buildData = useCallback(() => {
    const data = [];
    const periods = Array.isArray(slotList) ? slotList : [];
    for (let i = 0; i < SLOT_COUNT; i++) {
      const startMin = i * SLOT_MIN;
      const styleObj = getTimeSlotsClass(startMin, slotList, activePeriodGroup?.periodColor);
      // Identify period metadata
      let periodIndex = -1; let periodLabel = ''; let periodColor = styleObj?.backgroundColor || 'transparent';
      for (let p = 0; p < periods.length; p++) {
        const match = getPeriodMatch(periods[p], startMin, p);
        if (match) { periodIndex = p; periodColor = match.periodColor || periodColor; periodLabel = match.periodLabel; break; }
      }
      data.push({
        index: i,
        start: startMin,
        end: startMin + SLOT_MIN,
        color: styleObj?.backgroundColor || 'transparent',
        periodIndex,
        periodLabel,
        periodColor
      });
    }
    return data;
  }, [slotList, activePeriodGroup, slotSignature]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w && Math.abs(w - width) > 2) setWidth(w);
    };
    measure();
    let ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(measure); ro.observe(el);
    } else { window.addEventListener('resize', measure); }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, [width]);

  const draw = useCallback(() => {
    const svgEl = svgRef.current; if (!svgEl) return;
    const tooltipEl = tooltipRef.current;
    const data = buildData();
    const innerWidth = Math.max(200, width - MARGIN.left - MARGIN.right);
    const height = MARGIN.top + CELL_HEIGHT + MARGIN.bottom;
    const svg = d3.select(svgEl)
      .attr('width', width)
      .attr('height', height)
      .style('background','transparent')
      .style('overflow','visible');

    const isActiveSlot = (d) => d.periodIndex >= 0 && d.color && d.color !== 'transparent';
    const hideTooltip = () => { if (tooltipEl) tooltipEl.style.display = 'none'; };
    const updateTooltip = (event, d) => {
      if (!tooltipEl || !isActiveSlot(d)) { hideTooltip(); return; }
      const label = formatTimeLabel(d.start);
      tooltipEl.style.display = 'block';
      tooltipEl.innerHTML = `<strong>${label}</strong><br/><div class="tsd-period-info"><span class="tsd-period-swatch" style="background:${d.periodColor}"></span><span class="tsd-period-label">${d.periodLabel}</span></div>`;
      const container = containerRef.current; if (!container) return;
      const cRect = container.getBoundingClientRect();
      const ttW = tooltipEl.offsetWidth; const ttH = tooltipEl.offsetHeight;
      if (tooltipFrameRef.current) cancelAnimationFrame(tooltipFrameRef.current);
      tooltipFrameRef.current = requestAnimationFrame(() => {
        let xPos = event.clientX - cRect.left + 12;
        const maxX = cRect.width - ttW - 2; if (xPos < 0) xPos = 0; else if (xPos > maxX) xPos = maxX;
        let yPos = event.clientY - cRect.top - ttH - 12; if (yPos < 0) yPos = event.clientY - cRect.top + 16;
        tooltipEl.style.left = `${xPos}px`; tooltipEl.style.top = `${yPos}px`;
      });
    };

    svg.selectAll('*').remove();
    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);
    const x = d3.scaleLinear().domain([0, TOTAL_MINUTES]).range([0, innerWidth]);

    // Slots
    g.append('g').attr('class','slots')
      .selectAll('rect.slot')
      .data(data)
      .enter().append('rect')
      .attr('class','slot')
      .attr('data-period-index', d => d.periodIndex)
      .attr('x', d => x(d.start))
      .attr('y', 0)
      .attr('width', d => Math.max(0, x(d.end)-x(d.start)))
      .attr('height', CELL_HEIGHT)
      .attr('fill', d => d.color)
      .attr('stroke', dark ? '#222' : '#ddd')
      .attr('stroke-width', 0.4)
      .style('cursor', d => (isActiveSlot(d) ? 'pointer':'default'))
      .style('transition','transform 140ms ease-out, stroke-width 120ms ease-out')
      .on('mouseenter', (event, d) => {
        if (!isActiveSlot(d)) return;
        d3.select(event.currentTarget)
          .raise()
          .attr('transform', `translate(0,${-3}) scale(1, ${ (CELL_HEIGHT + 6) / CELL_HEIGHT })`)
          .attr('stroke-width', 1)
          .attr('stroke', dark ? '#888' : '#555');
      })
      .on('mousemove', (event, d) => updateTooltip(event, d))
      .on('mouseleave', (event) => {
        hideTooltip();
        d3.select(event.currentTarget)
          .attr('transform', null)
          .attr('stroke-width',0.4)
          .attr('stroke', dark ? '#222':'#ddd');
      });

    // Hour lines
    const hourGroup = g.append('g').attr('class','hour-lines');
    for (let h=0; h<=24; h++) {
      const xPos = x(h*60);
      hourGroup.append('line')
        .attr('x1', xPos).attr('x2', xPos)
        .attr('y1', 0).attr('y2', CELL_HEIGHT)
        .attr('stroke', dark ? '#444':'#bbb')
        .attr('stroke-width', (h===0 || h===24) ? 1 : 0.6)
        .attr('opacity', (h===0 || h===24) ? 1 : 0.9);
    }

    // Axis (flush)
    const xAxis = d3.axisBottom(x).tickValues(d3.range(0, TOTAL_MINUTES+60, 60)).tickFormat(d=>d/60);
    const axisG = g.append('g').attr('transform',`translate(0,${CELL_HEIGHT})`).call(xAxis);
    axisG.selectAll('path.domain').attr('stroke', dark ? '#555':'#888');
    axisG.selectAll('line').attr('stroke', dark ? '#333':'#ccc').attr('y2',4);
    axisG.selectAll('text').style('fill', dark ? '#eee':'#222').style('font-size','11px').style('font-family','Nunito, sans-serif');
  }, [buildData, width, colorScheme, slotSignature]);

  useEffect(()=>{ draw(); }, [draw]);

  // Global container handlers to avoid sticky transforms when mouse moves fast
  useEffect(() => {
    const container = containerRef.current;
    const svgEl = svgRef.current;
    if (!container || !svgEl) return;

    function onMove(e) {
      const rects = svgEl.querySelectorAll('rect.slot');
      rects.forEach(r => {
        r.removeAttribute('transform');
        r.setAttribute('stroke-width', 0.4);
        r.setAttribute('stroke', dark ? '#222' : '#ddd');
      });
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || !el.classList.contains('slot')) return;
      const periodIndex = Number(el.getAttribute('data-period-index'));
      const fill = el.getAttribute('fill');
      if (Number.isNaN(periodIndex) || periodIndex < 0) return;
      if (!fill || fill === 'transparent') return;
      el.setAttribute('transform', `translate(0,${-3}) scale(1, ${ (CELL_HEIGHT + 6) / CELL_HEIGHT })`);
      el.setAttribute('stroke-width', 1);
      el.setAttribute('stroke', dark ? '#888' : '#555');
    }

    function onLeave() {
      const rects = svgEl.querySelectorAll('rect.slot');
      rects.forEach(r => {
        r.removeAttribute('transform');
        r.setAttribute('stroke-width', 0.4);
        r.setAttribute('stroke', dark ? '#222' : '#ddd');
      });
      const t = tooltipRef.current; if (t) t.style.display = 'none';
    }

    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseleave', onLeave);
    return () => {
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseleave', onLeave);
    };
  }, [dark, slotSignature]);

  return (
    <div className={`row mt-2 tariff-time-slots-root ${dark ? 'dark' : ''}`}>
      <div className="col-sm-12 mb-4">
        <label className="me-3 flex-shrink-0 tariff-time-slots-label" style={{fontWeight:700}}>
          {`${selectedSeason?.name ?? ''} : ${t("tariff.timeSlots.progress24h")}`}
        </label>
        <div ref={containerRef} className="tariff-time-slots-container">
          <svg ref={svgRef} role="img" aria-label="24 hour 15 minute slot chart" />
          <div ref={tooltipRef} className="time-slots-tooltip" />
        </div>
      </div>
    </div>
  );
};

export default TimeSlotsDisplay;
